/**
 * Panel manager - handles tower/building selection, level up, and mine panels
 */

import { Vector } from '../../../core/math/vector';
import { Circle } from '../../../core/math/circle';
import { EffectText } from '../../../effects/effect';
import { TOWER_IMG_WIDTH, TOWER_IMG_HEIGHT, TOWER_IMG_PRE_WIDTH, TOWER_IMG_PRE_HEIGHT } from '../../../towers/index';
import { TowerRegistry } from '../../../towers/towerRegistry';
import { BuildingRegistry } from '../../../buildings/index';
import type { MonsterSpawner } from '../../../buildings/variants/monsterSpawner';
import { Mine } from '../../../systems/energy/mine';
import { VisionType } from '../../../systems/fog/visionConfig';
import { SpawnerPanel } from './spawnerPanel';
import { ManualCannonPanel } from './manualCannonPanel';
import type { TowerManualCannon } from '../../../towers/base/towerManualCannon';
import type { GameEntity, CanvasWithInputHandler, PanelManagerWorldLike, PanelEntityLike, PanelCircleLike } from './types';
import type { NetworkClient } from '../../../network/networkClient';
import type { MultiplayerWorldFacade } from './multiplayerWorldFacade';
import { getTowerCombatData } from '@shared/config/towerCombatMeta';
import { getBuildingMeta } from '@shared/config/buildingMeta';

const LEVELUP_POOL_SIZE = 10;

// Move mode constants
const MOVE_COST = 200;           // 移动费用
const MOVE_MAX_DISTANCE = 75;    // 最大移动距离
const ICON_RATE = 0.5;

/** Stable identity and local display data for the currently selected placement. */
export interface PlacementSelection {
    kind: 'tower' | 'building';
    typeId: string;
    price: number;
    radius: number;
}

/**
 * Calculate image sprite position from index
 */
function getTowerImgPosition(imgIndex: number): { x: number; y: number } {
    const cols = Math.floor(TOWER_IMG_WIDTH / TOWER_IMG_PRE_WIDTH);
    const x = (imgIndex % cols) * TOWER_IMG_PRE_WIDTH;
    const y = Math.floor(imgIndex / cols) * TOWER_IMG_PRE_HEIGHT;
    return { x, y };
}

export interface PanelManagerCallbacks {
    requestPauseRender: () => void;
    getGameEnd: () => boolean;
}

export class PanelManager {
    private world: PanelManagerWorldLike;
    private canvasEle: CanvasWithInputHandler;
    private callbacks: PanelManagerCallbacks;
    private sessionId: string;
    private eventSignal: AbortSignal;

    // Panel DOM elements
    private choiceBtn: HTMLElement;
    private smallLevelUpPanelEle: HTMLElement;
    private listEle: HTMLElement;
    private otherItemsEle: HTMLElement;

    // DOM cache pool
    private levelUpItemPool: HTMLElement[] = [];
    private cachedLevelDownBtn!: HTMLElement;
    private cachedSellBtn!: HTMLElement;
    private cachedObserverBtn!: HTMLElement;
    private cachedRadarBtn!: HTMLElement;

    // Current panel state
    private currentPanelEntity: GameEntity | null = null;
    private currentClickPos: Vector | null = null;
    private currentPanelMine: Mine | null = null;
    private currentMineScreenPos: Vector | null = null;

    // Selection state
    private addedThingFunc: ((world: unknown) => GameEntity) | null = null;
    private placementSelection: PlacementSelection | null = null;
    private selectedThing: GameEntity | null = null;
    private cachedBuilding: GameEntity | null = null;
    private lastAddedFunc: ((world: unknown) => GameEntity) | null = null;

    // Move mode state
    private moveMode: boolean = false;
    private moveTarget: GameEntity | null = null;

    // 扳手光标 SVG (用于移动模式)
    private static readonly WRENCH_CURSOR = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z'/%3E%3C/svg%3E") 12 12, pointer`;

    // RAF scheduling
    private pendingPanelRAF: number | null = null;
    private pendingPanelUpdate: (() => void) | null = null;

    // Intervals
    private refreshPanelInterval: ReturnType<typeof setInterval> | null = null;
    private freshBtnInterval: ReturnType<typeof setInterval> | null = null;

    // Spawner panel
    private spawnerPanel: SpawnerPanel;

    // Manual cannon panel
    private manualCannonPanel: ManualCannonPanel;

    // Network client for multiplayer mode (null = single-player)
    private networkClient: NetworkClient | null = null;

    constructor(
        world: PanelManagerWorldLike,
        canvasEle: CanvasWithInputHandler,
        sessionId: string,
        eventSignal: AbortSignal,
        callbacks: PanelManagerCallbacks,
        networkClient?: NetworkClient
    ) {
        this.world = world;
        this.canvasEle = canvasEle;
        this.sessionId = sessionId;
        this.eventSignal = eventSignal;
        this.callbacks = callbacks;
        this.networkClient = networkClient ?? null;

        this.choiceBtn = document.querySelector(".choiceBtn") as HTMLElement;
        this.smallLevelUpPanelEle = document.querySelector("#smallLevelUpPanel") as HTMLElement;
        this.listEle = this.smallLevelUpPanelEle.querySelector(".levelUpItems") as HTMLElement;
        this.otherItemsEle = this.smallLevelUpPanelEle.querySelector(".otherItems") as HTMLElement;

        // Initialize spawner panel
        this.spawnerPanel = new SpawnerPanel(eventSignal);

        // Initialize manual cannon panel
        this.manualCannonPanel = new ManualCannonPanel(eventSignal, this.networkClient ?? undefined, canvasEle);
    }

    /**
     * Initialize panel manager
     */
    init(): void {
        this.initDOMCache();
        this.bindCanvasEvents();
        this.bindLevelUpPanelEvents();
        this.startIntervals();
        this.setupRightClickHandler();
    }

    /**
     * Cleanup intervals and handlers
     */
    destroy(): void {
        if (this.refreshPanelInterval) {
            clearInterval(this.refreshPanelInterval);
            this.refreshPanelInterval = null;
        }
        if (this.freshBtnInterval) {
            clearInterval(this.freshBtnInterval);
            this.freshBtnInterval = null;
        }
        // Cleanup spawner panel
        this.spawnerPanel.destroy();
        // Cleanup manual cannon panel
        this.manualCannonPanel.destroy();
        // Cancel pending RAF
        if (this.pendingPanelRAF !== null) {
            cancelAnimationFrame(this.pendingPanelRAF);
            this.pendingPanelRAF = null;
        }
        this.pendingPanelUpdate = null;
        // 恢复默认光标
        this.setMoveCursor(false);
    }

    /**
     * 设置/恢复移动模式光标
     */
    private setMoveCursor(enabled: boolean): void {
        this.canvasEle.style.cursor = enabled ? PanelManager.WRENCH_CURSOR : '';
    }

    /**
     * Get current added thing function
     */
    getAddedThingFunc(): ((world: unknown) => GameEntity) | null {
        return this.addedThingFunc;
    }

    getPlacementSelection(): PlacementSelection | null {
        return this.placementSelection;
    }

    /**
     * Get current selected thing
     */
    getSelectedThing(): GameEntity | null {
        return this.selectedThing;
    }

    private schedulePanelUpdate(updateFn: () => void): void {
        this.pendingPanelUpdate = updateFn;
        if (this.pendingPanelRAF === null) {
            this.pendingPanelRAF = requestAnimationFrame(() => {
                this.pendingPanelRAF = null;
                if (this.pendingPanelUpdate) {
                    this.pendingPanelUpdate();
                    this.pendingPanelUpdate = null;
                }
            });
        }
    }

    private createLevelUpItem(): HTMLElement {
        const divLevelUpItemEle = document.createElement("div");
        divLevelUpItemEle.classList.add("levelUpItem");
        const nameDiv = document.createElement("div");
        nameDiv.classList.add("name");
        divLevelUpItemEle.appendChild(nameDiv);
        const imgDiv = document.createElement("div");
        imgDiv.classList.add("icon");
        imgDiv.style.backgroundImage = "url('/towers/imgs/towers.png')";
        imgDiv.style.backgroundSize = `${TOWER_IMG_WIDTH * ICON_RATE}px ${TOWER_IMG_HEIGHT * ICON_RATE}px`;
        imgDiv.style.width = TOWER_IMG_PRE_WIDTH * ICON_RATE + "px";
        imgDiv.style.height = TOWER_IMG_PRE_HEIGHT * ICON_RATE + "px";
        imgDiv.style.outline = "solid 1px";
        divLevelUpItemEle.appendChild(imgDiv);
        const priceDiv = document.createElement("div");
        priceDiv.classList.add("price");
        divLevelUpItemEle.appendChild(priceDiv);
        return divLevelUpItemEle;
    }

    private initDOMCache(): void {
        // Create level up item pool
        for (let i = 0; i < LEVELUP_POOL_SIZE; i++) {
            this.levelUpItemPool.push(this.createLevelUpItem());
        }

        // Create level down button
        this.cachedLevelDownBtn = document.createElement("div");
        this.cachedLevelDownBtn.classList.add("item", "levelDown");
        const iconDiv = document.createElement("div");
        iconDiv.classList.add("icon");
        this.cachedLevelDownBtn.appendChild(iconDiv);
        const textDiv = document.createElement("div");
        textDiv.classList.add("inner-text");
        this.cachedLevelDownBtn.appendChild(textDiv);

        // Create sell button
        this.cachedSellBtn = document.createElement("div");
        this.cachedSellBtn.classList.add("item", "sell");
        const sellIconDiv = document.createElement("div");
        sellIconDiv.classList.add("icon");
        this.cachedSellBtn.appendChild(sellIconDiv);
        const sellTextDiv = document.createElement("div");
        sellTextDiv.classList.add("inner-text");
        this.cachedSellBtn.appendChild(sellTextDiv);

        // Create observer vision upgrade button
        this.cachedObserverBtn = document.createElement("div");
        this.cachedObserverBtn.classList.add("item", "visionUpgrade", "observer");
        this.cachedObserverBtn.setAttribute("data-vision-type", VisionType.OBSERVER.toString());
        const observerIconDiv = document.createElement("div");
        observerIconDiv.classList.add("icon");
        observerIconDiv.textContent = "👁";
        this.cachedObserverBtn.appendChild(observerIconDiv);
        const observerTextDiv = document.createElement("div");
        observerTextDiv.classList.add("inner-text");
        this.cachedObserverBtn.appendChild(observerTextDiv);

        // Create radar vision upgrade button
        this.cachedRadarBtn = document.createElement("div");
        this.cachedRadarBtn.classList.add("item", "visionUpgrade", "radar");
        this.cachedRadarBtn.setAttribute("data-vision-type", VisionType.RADAR.toString());
        const radarIconDiv = document.createElement("div");
        radarIconDiv.classList.add("icon");
        radarIconDiv.textContent = "📡";
        this.cachedRadarBtn.appendChild(radarIconDiv);
        const radarTextDiv = document.createElement("div");
        radarTextDiv.classList.add("inner-text");
        this.cachedRadarBtn.appendChild(radarTextDiv);
    }

    private bindLevelUpPanelEvents(): void {
        // Event delegation for listEle
        this.listEle.addEventListener("click", (e) => {
            const target = (e.target as HTMLElement).closest(".levelUpItem") as HTMLElement | null;
            if (!target) return;

            // Handle tower upgrade
            if (this.currentPanelEntity && this.currentClickPos) {
                const price = parseInt(target.dataset.price!);
                const towerName = target.dataset.towerName!;
                const isMultiplayer = this.networkClient !== null;

                if (isMultiplayer && this.currentPanelEntity.id) {
                    // Multiplayer: send upgrade request to server
                    this.networkClient!.upgradeTower({
                        towerId: this.currentPanelEntity.id,
                        targetType: towerName
                    });
                    this.hideLevelUpPanel();
                } else if (!isMultiplayer) {
                    // Single-player: local handling
                    if (this.world.spendMoney(price)) {
                        const pos = this.currentPanelEntity.pos.copy();
                        const newThing = TowerRegistry.create(towerName, this.world) as GameEntity;
                        newThing.towerLevel = this.currentPanelEntity.towerLevel + 1;
                        newThing.pos = pos;
                        // Preserve vision attributes
                        newThing.visionType = this.currentPanelEntity.visionType;
                        newThing.visionLevel = this.currentPanelEntity.visionLevel;
                        newThing.radarAngle = this.currentPanelEntity.radarAngle;
                        this.world.addTower(newThing);
                        this.currentPanelEntity.remove();
                        this.showSmallLevelUpPanel(newThing, this.currentClickPos);
                    } else {
                        const et = new EffectText("钱不够！");
                        et.pos = this.currentClickPos;
                        this.world.addEffect(et);
                    }
                }
                this.callbacks.requestPauseRender();
                return;
            }

            // Handle mine operations
            if (this.currentPanelMine && this.currentMineScreenPos) {
                const action = target.dataset.mineAction;
                const price = parseInt(target.dataset.price || "0");
                const isMultiplayer = this.networkClient !== null;

                if (action === "upgrade" || action === "repair") {
                    if (isMultiplayer && this.currentPanelMine.id) {
                        // Multiplayer: send mine action to server
                        const facade = this.world as unknown as {
                            upgradeMine?: (id: string) => void;
                            repairMine?: (id: string) => void;
                        };
                        if (action === "upgrade" && facade.upgradeMine) {
                            facade.upgradeMine(this.currentPanelMine.id);
                        } else if (action === "repair" && facade.repairMine) {
                            facade.repairMine(this.currentPanelMine.id);
                        }
                        this.hideLevelUpPanel();
                    } else if (!isMultiplayer) {
                        // Single-player: local handling
                        // Re-check territory validity (may have changed since panel opened)
                        if (!this.currentPanelMine.inValidTerritory) {
                            const et = new EffectText("不在有效领地，无法操作");
                            et.pos = this.currentPanelMine.pos.copy();
                            this.world.addEffect(et);
                            this.hideLevelUpPanel();
                            this.callbacks.requestPauseRender();
                            return;
                        }
                        if (this.world.spendMoney(price)) {
                            if (action === "upgrade") {
                                this.currentPanelMine.upgrade();
                            } else {
                                this.currentPanelMine.startRepair();
                            }
                            this.showMinePanel(this.currentPanelMine, this.currentMineScreenPos);
                        } else {
                            const et = new EffectText("钱不够！");
                            et.pos = this.currentPanelMine.pos.copy();
                            this.world.addEffect(et);
                        }
                    }
                }
                this.callbacks.requestPauseRender();
            }
        });

        // Event delegation for otherItemsEle
        this.otherItemsEle.addEventListener("click", (e) => {
            const target = (e.target as HTMLElement).closest(".item") as HTMLElement | null;
            if (!target) return;

            // Handle tower operations
            if (this.currentPanelEntity && this.currentClickPos) {
                const isMultiplayer = this.networkClient !== null;

                if (target.classList.contains("levelDown")) {
                    if (isMultiplayer) {
                        // Multiplayer: level down not supported yet
                        const et = new EffectText("多人模式暂不支持降级");
                        et.pos = this.currentClickPos;
                        this.world.addEffect(et);
                    } else {
                        // Single-player: local handling
                        const towerName = this.currentPanelEntity.levelDownGetter as string | null;
                        if (towerName === null) {
                            const et = new EffectText("无法降级！");
                            et.pos = this.currentClickPos;
                            this.world.addEffect(et);
                        } else {
                            const downObj = TowerRegistry.create(towerName, this.world) as GameEntity;
                            downObj.towerLevel = Math.max(1, this.currentPanelEntity.towerLevel - 1);
                            // Preserve vision attributes
                            downObj.visionType = this.currentPanelEntity.visionType;
                            downObj.visionLevel = this.currentPanelEntity.visionLevel;
                            downObj.radarAngle = this.currentPanelEntity.radarAngle;
                            this.world.addMoney(this.currentPanelEntity.price / 4);
                            const newPos = this.currentPanelEntity.pos.copy();
                            this.currentPanelEntity.remove();
                            downObj.pos = newPos;
                            this.world.addTower(downObj);
                            this.showSmallLevelUpPanel(downObj, this.currentClickPos);
                        }
                    }
                } else if (target.classList.contains("sell")) {
                    if (isMultiplayer && this.currentPanelEntity.id) {
                        // Multiplayer: route through facade for client prediction
                        if (this.world.sellTower) {
                            this.world.sellTower(this.currentPanelEntity.id);
                        } else {
                            this.networkClient!.sellTower({
                                towerId: this.currentPanelEntity.id
                            });
                        }
                        this.hideLevelUpPanel();
                    } else if (!isMultiplayer) {
                        // Single-player: local handling
                        const refund = this.currentPanelEntity.getSellRefund?.() ?? Math.floor(this.currentPanelEntity.price / 2);
                        this.world.addMoney(refund);
                        this.currentPanelEntity.remove();
                        this.hideLevelUpPanel();
                    }
                } else if (target.classList.contains("visionUpgrade")) {
                    const visionType = (target.dataset.visionType || VisionType.NONE) as VisionType;
                    if (this.networkClient && this.currentPanelEntity.id) {
                        // Multiplayer: send to server
                        this.networkClient.sendUpgradeVision({
                            towerId: this.currentPanelEntity.id,
                            visionType: visionType as 'observer' | 'radar',
                        });
                    } else if (this.currentPanelEntity.canUpgradeVision?.(visionType)) {
                        // Single-player: local handling
                        const price = this.currentPanelEntity.getVisionUpgradePrice?.(visionType) ?? 0;
                        if (this.world.spendMoney(price)) {
                            this.currentPanelEntity.upgradeVision?.(visionType);
                            this.world.fog?.markDirty?.();
                            this.showSmallLevelUpPanel(this.currentPanelEntity, this.currentClickPos);
                        } else {
                            const et = new EffectText("金币不足！");
                            et.pos = this.currentPanelEntity.pos.copy();
                            this.world.addEffect(et);
                        }
                    }
                }
                this.callbacks.requestPauseRender();
                return;
            }

            // Handle mine operations
            if (this.currentPanelMine && this.currentMineScreenPos) {
                const isMultiplayer = this.networkClient !== null;
                const facade = this.world as unknown as {
                    downgradeMine?: (id: string) => void;
                    sellMine?: (id: string) => void;
                };

                if (target.classList.contains("levelDown")) {
                    if (isMultiplayer && this.currentPanelMine.id && facade.downgradeMine) {
                        facade.downgradeMine(this.currentPanelMine.id);
                        this.hideLevelUpPanel();
                    } else if (!isMultiplayer) {
                        const refund = parseInt(target.dataset.refund || "0");
                        this.world.addMoney(refund);
                        this.currentPanelMine.downgrade();
                        this.showMinePanel(this.currentPanelMine, this.currentMineScreenPos);
                    }
                } else if (target.classList.contains("sell")) {
                    if (isMultiplayer && this.currentPanelMine.id && facade.sellMine) {
                        facade.sellMine(this.currentPanelMine.id);
                        this.hideLevelUpPanel();
                    } else if (!isMultiplayer) {
                        const sellPrice = parseInt(target.dataset.sellPrice || "0");
                        this.world.addMoney(sellPrice);
                        this.currentPanelMine.destroy();
                        this.hideLevelUpPanel();
                    }
                }
                this.callbacks.requestPauseRender();
            }
        });

        // Mouse leave event
        this.smallLevelUpPanelEle.addEventListener("mouseleave", () => {
            this.hideLevelUpPanel();
        });
    }

    private showInitPanel(): void {
        const panelEle = document.querySelector(".initPanel") as HTMLElement & { dataset: { sessionId?: string } };

        if (panelEle.dataset.sessionId !== this.sessionId) {
            panelEle.innerHTML = "";
            panelEle.dataset.sessionId = this.sessionId;
            const things: Array<{ kind: PlacementSelection['kind']; typeId: string; creator: (world: unknown) => GameEntity }> = [];
            const basicCreator = TowerRegistry.getCreator('BasicCannon') as ((world: unknown) => GameEntity) | undefined;
            if (basicCreator) things.push({ kind: 'tower', typeId: 'BasicCannon', creator: basicCreator });
            const buildingIds = ['Collector', 'Treatment', 'MonsterSpawner'];
            for (const typeId of buildingIds) {
                const creator = BuildingRegistry.getCreator(typeId) as ((world: unknown) => GameEntity) | undefined;
                if (creator && (typeId !== 'MonsterSpawner' || this.networkClient !== null)) {
                    things.push({ kind: 'building', typeId, creator });
                }
            }
            for (const thing of things) {
                const btn = document.createElement('button');
                btn.classList.add("towerBtn");
                const b = thing.creator(this.world);
                btn.innerHTML = b.name + `<br>${b.price}￥`;
                btn.classList.add(b.gameType);
                btn.setAttribute("data-price", b.price.toString());
                btn.addEventListener("click", () => {
                    const towerMeta = thing.kind === 'tower' ? getTowerCombatData(thing.typeId) : undefined;
                    const buildingMeta = thing.kind === 'building' ? getBuildingMeta(thing.typeId) : undefined;
                    this.placementSelection = {
                        kind: thing.kind,
                        typeId: thing.typeId,
                        price: b.price,
                        radius: towerMeta?.radius ?? buildingMeta?.radius ?? b.r ?? 15,
                    };
                    this.addedThingFunc = thing.creator;
                    // 清除移动模式状态
                    this.moveMode = false;
                    this.moveTarget = null;
                    this.world.user.moveTarget = null;
                    this.setMoveCursor(false);
                });
                panelEle.appendChild(btn);
            }
            // 移动工具按钮
            const moveBtn = document.createElement('button');
            moveBtn.innerText = "移动工具\n" + MOVE_COST + "￥";
            moveBtn.classList.add("towerBtn", "moveTool");
            moveBtn.setAttribute("data-price", MOVE_COST.toString());
            if (this.networkClient !== null) {
                moveBtn.disabled = true;
                moveBtn.title = "多人模式暂不支持移动工具";
            }
            moveBtn.addEventListener("click", () => {
                if (this.networkClient !== null) {
                    const et = new EffectText("多人模式暂不支持移动工具");
                    et.pos = new Vector(this.world.width / 2, this.world.height / 2);
                    this.world.addEffect(et);
                    this.callbacks.requestPauseRender();
                    return;
                }
                this.moveMode = true;
                this.moveTarget = null;
                this.world.user.moveTarget = null;
                    this.addedThingFunc = null;
                    this.placementSelection = null;
                this.world.user.putLoc.building = null;
                this.cachedBuilding = null;
                this.lastAddedFunc = null;
                this.setMoveCursor(true);
            });
            panelEle.appendChild(moveBtn);

            const cancelBtn = document.createElement("button");
            cancelBtn.innerText = "取消放置模式";
            cancelBtn.id = "cancelSelect";
            cancelBtn.addEventListener("click", () => {
                this.addedThingFunc = null;
                this.placementSelection = null;
                this.moveMode = false;
                this.moveTarget = null;
                this.world.user.moveTarget = null;
                this.world.user.putLoc.building = null;
                this.cachedBuilding = null;
                this.lastAddedFunc = null;
                this.setMoveCursor(false);
                this.callbacks.requestPauseRender();
            });
            panelEle.appendChild(cancelBtn);
        }

        if (panelEle.style.display === "block") {
            return;
        }
        this.hideAllPanel();
        panelEle.style.display = "block";
    }

    private showSelectedPanel(forceAble: boolean): void {
        const panelEle = document.querySelector(".choicePanel") as HTMLElement;
        if (panelEle.style.display === "block") {
            if (!forceAble) {
                return;
            }
        }
        this.hideAllPanel();
        panelEle.style.display = "block";
        if (panelEle.innerHTML !== "") {
            const hideAllData = () => {
                for (let i = 0; i < panelEle.children.length; i++) {
                    (panelEle.children[i] as HTMLElement).style.display = "none";
                }
            };
            if (this.selectedThing!.gameType === "Monster") {
                hideAllData();
                (panelEle.querySelector(".monsterData") as HTMLElement).style.display = "block";
                (panelEle.querySelector(".monsterName") as HTMLElement).innerHTML = this.selectedThing!.name;
                (panelEle.querySelector(".monsterComment") as HTMLElement).innerHTML = this.selectedThing!.comment || "";
            }
        }
    }

    private hideAllPanel(): void {
        for (let i = 0; i < this.choiceBtn.children.length; i++) {
            (this.choiceBtn.children[i] as HTMLElement).style.display = "none";
        }
    }

    private hideLevelUpPanel(): void {
        this.smallLevelUpPanelEle.style.display = "none";
    }

    showSmallLevelUpPanel(thing: GameEntity, clickPos: Vector): void {
        if (thing.inValidTerritory === false) {
            const et = new EffectText("无效领地内无法操作！");
            et.pos = thing.pos.copy();
            this.world.addEffect(et);
            this.callbacks.requestPauseRender();
            return;
        }

        this.currentPanelEntity = thing;
        this.currentClickPos = clickPos;
        this.currentPanelMine = null;
        this.currentMineScreenPos = null;

        this.schedulePanelUpdate(() => {
            this.smallLevelUpPanelEle.style.display = "block";
            this.smallLevelUpPanelEle.style.left = clickPos.x + 10 + "px";
            this.smallLevelUpPanelEle.style.top = clickPos.y + 10 + "px";
            const nameSpan = this.smallLevelUpPanelEle.querySelector(".name") as HTMLElement;
            nameSpan.innerHTML = thing.name;

            // Clear and rebuild using pool
            while (this.listEle.firstChild) {
                this.listEle.removeChild(this.listEle.firstChild);
            }

            if (thing.levelUpArr.length === 0) {
                const tips = document.createElement("p");
                tips.innerText = '这个建筑已经顶级啦！';
                this.listEle.appendChild(tips);
            } else {
                let index = 0;
                for (const towerName of thing.levelUpArr as string[]) {
                    const meta = TowerRegistry.getMeta(towerName);
                    if (!meta) continue;

                    let itemEle: HTMLElement;
                    if (index < this.levelUpItemPool.length) {
                        itemEle = this.levelUpItemPool[index];
                    } else {
                        itemEle = this.createLevelUpItem();
                        this.levelUpItemPool.push(itemEle);
                    }

                    const nameDiv = itemEle.querySelector(".name") as HTMLElement;
                    nameDiv.innerText = meta.name;
                    const imgDiv = itemEle.querySelector(".icon") as HTMLElement;
                    imgDiv.style.display = "";
                    const diffPos = getTowerImgPosition(meta.imgIndex);
                    imgDiv.style.backgroundPositionX = -diffPos.x * ICON_RATE + "px";
                    imgDiv.style.backgroundPositionY = -diffPos.y * ICON_RATE + "px";
                    const priceDiv = itemEle.querySelector(".price") as HTMLElement;
                    priceDiv.innerText = `${meta.basePrice}元`;

                    itemEle.setAttribute("data-price", meta.basePrice.toString());
                    itemEle.setAttribute("data-tower-name", towerName);

                    this.listEle.appendChild(itemEle);
                    index++;
                }
            }

            // Update other items
            while (this.otherItemsEle.firstChild) {
                this.otherItemsEle.removeChild(this.otherItemsEle.firstChild);
            }

            const levelDownText = this.cachedLevelDownBtn.querySelector(".inner-text") as HTMLElement;
            levelDownText.innerHTML = `降级<br>+${thing.price / 4}元`;
            this.otherItemsEle.appendChild(this.cachedLevelDownBtn);

            const sellText = this.cachedSellBtn.querySelector(".inner-text") as HTMLElement;
            const sellRefund = thing.getSellRefund?.() ?? Math.floor(thing.price / 2);
            sellText.innerHTML = `卖了<br>+${sellRefund}元`;
            this.otherItemsEle.appendChild(this.cachedSellBtn);

            // Vision upgrade buttons
            if (thing.gameType === "Tower") {
                if (thing.visionType === VisionType.NONE || thing.visionType === VisionType.OBSERVER) {
                    if (thing.canUpgradeVision?.(VisionType.OBSERVER)) {
                        const price = thing.getVisionUpgradePrice?.(VisionType.OBSERVER) ?? 0;
                        const observerText = this.cachedObserverBtn.querySelector(".inner-text") as HTMLElement;
                        const currentLevel = thing.visionType === VisionType.OBSERVER ? (thing.visionLevel ?? 0) : 0;
                        observerText.innerHTML = `观察塔${currentLevel > 0 ? 'Lv' + (currentLevel + 1) : ''}<br>${price}元`;
                        this.otherItemsEle.appendChild(this.cachedObserverBtn);
                    }
                }
                if (thing.visionType === VisionType.NONE || thing.visionType === VisionType.RADAR) {
                    if (thing.canUpgradeVision?.(VisionType.RADAR)) {
                        const price = thing.getVisionUpgradePrice?.(VisionType.RADAR) ?? 0;
                        const radarText = this.cachedRadarBtn.querySelector(".inner-text") as HTMLElement;
                        const currentLevel = thing.visionType === VisionType.RADAR ? (thing.visionLevel ?? 0) : 0;
                        radarText.innerHTML = `雷达塔${currentLevel > 0 ? 'Lv' + (currentLevel + 1) : ''}<br>${price}元`;
                        this.otherItemsEle.appendChild(this.cachedRadarBtn);
                    }
                }
            }
        });
    }

    showMinePanel(mine: Mine, screenPos: Vector): void {
        // Check mine's own territory validity, not just position
        if (!mine.inValidTerritory) {
            const et = new EffectText("不在有效领地，无法操作");
            et.pos = mine.pos.copy();
            this.world.addEffect(et);
            this.callbacks.requestPauseRender();
            return;
        }

        this.currentPanelMine = mine;
        this.currentMineScreenPos = screenPos;
        this.currentPanelEntity = null;
        this.currentClickPos = null;

        this.schedulePanelUpdate(() => {
            this.smallLevelUpPanelEle.style.display = "block";
            this.smallLevelUpPanelEle.style.left = screenPos.x + 10 + "px";
            this.smallLevelUpPanelEle.style.top = screenPos.y + 10 + "px";
            const nameSpan = this.smallLevelUpPanelEle.querySelector(".name") as HTMLElement;

            while (this.listEle.firstChild) {
                this.listEle.removeChild(this.listEle.firstChild);
            }
            while (this.otherItemsEle.firstChild) {
                this.otherItemsEle.removeChild(this.otherItemsEle.firstChild);
            }

            if (mine.state === Mine.STATE_NORMAL) {
                nameSpan.innerHTML = "矿井";
                const upgradePrice = mine.getUpgradePrice();
                const upgradeBtn = this.levelUpItemPool[0];
                const nameDiv = upgradeBtn.querySelector(".name") as HTMLElement;
                nameDiv.innerText = "升级为发电厂";
                const iconDiv = upgradeBtn.querySelector(".icon") as HTMLElement;
                iconDiv.style.display = "none";
                const priceDiv = upgradeBtn.querySelector(".price") as HTMLElement;
                priceDiv.innerText = `${upgradePrice}元`;
                upgradeBtn.setAttribute("data-price", upgradePrice!.toString());
                upgradeBtn.setAttribute("data-mine-action", "upgrade");
                this.listEle.appendChild(upgradeBtn);

            } else if (mine.state === Mine.STATE_DAMAGED) {
                nameSpan.innerHTML = "毁坏的矿井";
                if (mine.repairing) {
                    const progressText = document.createElement("p");
                    progressText.innerText = `修复中... ${Math.floor(mine.repairProgress / mine.REPAIR_TIME * 100)}%`;
                    this.listEle.appendChild(progressText);
                } else {
                    const repairBtn = this.levelUpItemPool[0];
                    const nameDiv = repairBtn.querySelector(".name") as HTMLElement;
                    nameDiv.innerText = "修复矿井";
                    const iconDiv = repairBtn.querySelector(".icon") as HTMLElement;
                    iconDiv.style.display = "none";
                    const priceDiv = repairBtn.querySelector(".price") as HTMLElement;
                    priceDiv.innerText = `${mine.REPAIR_COST}元`;
                    repairBtn.setAttribute("data-price", mine.REPAIR_COST.toString());
                    repairBtn.setAttribute("data-mine-action", "repair");
                    this.listEle.appendChild(repairBtn);
                }

            } else if (mine.state === Mine.STATE_POWER_PLANT) {
                nameSpan.innerHTML = `${mine.powerPlantLevel}级发电厂`;

                const upgradePrice = mine.getUpgradePrice();
                if (upgradePrice !== null) {
                    const upgradeBtn = this.levelUpItemPool[0];
                    const nameDiv = upgradeBtn.querySelector(".name") as HTMLElement;
                    nameDiv.innerText = `升级到${mine.powerPlantLevel + 1}级`;
                    const iconDiv = upgradeBtn.querySelector(".icon") as HTMLElement;
                    iconDiv.style.display = "none";
                    const priceDiv = upgradeBtn.querySelector(".price") as HTMLElement;
                    priceDiv.innerText = `${upgradePrice}元`;
                    upgradeBtn.setAttribute("data-price", upgradePrice.toString());
                    upgradeBtn.setAttribute("data-mine-action", "upgrade");
                    this.listEle.appendChild(upgradeBtn);
                } else {
                    const maxLevelText = document.createElement("p");
                    maxLevelText.innerText = "已经是最高等级！";
                    this.listEle.appendChild(maxLevelText);
                }

                const downgradeRefund = mine.getDowngradeRefund();
                const downgradeText = mine.powerPlantLevel > 1
                    ? `降为${mine.powerPlantLevel - 1}级`
                    : "降为矿井";
                const levelDownTextEl = this.cachedLevelDownBtn.querySelector(".inner-text") as HTMLElement;
                levelDownTextEl.innerHTML = `${downgradeText}<br>+${downgradeRefund}元`;
                this.cachedLevelDownBtn.setAttribute("data-refund", downgradeRefund.toString());
                this.otherItemsEle.appendChild(this.cachedLevelDownBtn);

                const sellPrice = mine.getSellPrice();
                const sellTextEl = this.cachedSellBtn.querySelector(".inner-text") as HTMLElement;
                sellTextEl.innerHTML = `卖了<br>+${sellPrice}元`;
                this.cachedSellBtn.setAttribute("data-sell-price", sellPrice.toString());
                this.otherItemsEle.appendChild(this.cachedSellBtn);
            }
        });
    }

    private bindCanvasEvents(): void {
        // Canvas click handler
        this.canvasEle.addEventListener('click', (e) => {
            try {
                const inputHandler = this.canvasEle._inputHandler;
                if (inputHandler?.wasDragging()) {
                    return;
                }
                const rect = this.canvasEle.getBoundingClientRect();
                const screenPos = new Vector(e.clientX - rect.left, e.clientY - rect.top);
                const clickPos = this.world.camera.screenToWorld(screenPos);

                // 移动模式处理
                if (this.moveMode) {
                    this.handleMoveMode(clickPos, screenPos);
                    this.callbacks.requestPauseRender();
                    return;
                }

                // Manual cannon targeting mode handling
                if (this.manualCannonPanel.isInTargetingMode() && this.manualCannonPanel.getCurrentCannon()) {
                    const handled = this.manualCannonPanel.handleTargetSelected(clickPos);
                    if (handled) {
                        this.callbacks.requestPauseRender();
                        return;
                    }
                }

                if (this.addedThingFunc === null) {
                    for (const item of this.world.getAllBuildingArr()) {
                        if (item.getBodyCircle().pointIn(clickPos.x, clickPos.y)) {
                            const ownerId = (item as PanelEntityLike & { ownerId?: string }).ownerId;
                            if (this.networkClient && ownerId && ownerId !== this.networkClient.gameSessionId) {
                                this.hideLevelUpPanel();
                                return;
                            }
                            if (item.gameType === "Mine") {
                                this.showMinePanel(item as Mine, screenPos);
                                return;
                            }
                            // Check for MonsterSpawner
                            if (item.canSpawnMonsters) {
                                if (this.networkClient && item.id) {
                                    const facade = this.world as MultiplayerWorldFacade;
                                    this.spawnerPanel.showNetwork(item.id, facade.getEnemyPlayers(), screenPos, this.networkClient);
                                } else {
                                    this.spawnerPanel.show(item as unknown as MonsterSpawner, screenPos);
                                }
                                return;
                            }
                            // Check for ManualCannon tower
                            if (item.canAttackBuildings && item.gameType === "Tower") {
                                this.manualCannonPanel.show(item as unknown as TowerManualCannon, screenPos);
                                item.selected = true;
                                return;
                            }
                            this.showSmallLevelUpPanel(item as unknown as GameEntity, screenPos);
                            item.selected = true;
                            return;
                        }
                    }
                    for (const mine of this.world.mines as Iterable<PanelEntityLike>) {
                        if (mine.getBodyCircle().pointIn(clickPos.x, clickPos.y)) {
                            this.showMinePanel(mine as unknown as Mine, screenPos);
                            return;
                        }
                    }
                    for (const item of this.world.monsters as Iterable<PanelEntityLike>) {
                        if (item.getBodyCircle().pointIn(clickPos.x, clickPos.y)) {
                            this.selectedThing = item as unknown as GameEntity;
                            this.showSelectedPanel(true);
                            item.selected = true;
                            return;
                        }
                    }
                    this.selectedThing = null;
                    this.hideLevelUpPanel();
                } else {
                    const addedThing = this.addedThingFunc(this.world);
                    const selection = this.placementSelection;
                    if (addedThing.canSpawnMonsters && this.networkClient === null) {
                        const et = new EffectText("怪物生成塔仅多人模式可用");
                        et.pos = clickPos.copy();
                        this.world.addEffect(et);
                        this.addedThingFunc = null;
                        this.placementSelection = null;
                        this.world.user.putLoc.building = null;
                        this.cachedBuilding = null;
                        this.lastAddedFunc = null;
                        return;
                    }
                    if (this.world.getMoney() < addedThing.price) {
                        const et = new EffectText("钱不够了！");
                        et.pos = clickPos.copy();
                        this.world.addEffect(et);
                        return;
                    }
                    addedThing.pos = clickPos;
                    for (const item of this.world.getAllBuildingArr()) {
                        if (addedThing.getBodyCircle().impact(item.getBodyCircle())) {
                            const et = new EffectText("这里不能放建筑，换一个地方点一下");
                            et.pos = addedThing.pos.copy();
                            this.world.addEffect(et);
                            return;
                        }
                    }
                    for (const obs of this.world.obstacles) {
                        if (obs.intersectsCircle(addedThing.getBodyCircle())) {
                            const et = new EffectText("这里有障碍物，不能放置建筑");
                            et.pos = addedThing.pos.copy();
                            this.world.addEffect(et);
                            return;
                        }
                    }
                    if (this.world.territory?.isPositionInValidTerritory && !this.world.territory.isPositionInValidTerritory(addedThing.pos)) {
                        const et = new EffectText("只能在有效领地内放置建筑");
                        et.pos = addedThing.pos.copy();
                        this.world.addEffect(et);
                        return;
                    }
                    this.world.spendMoney(addedThing.price);
                    switch (addedThing.gameType) {
                        case "Tower":
                            this.world.addTower(this.networkClient && selection
                                ? { towerType: selection.typeId, pos: clickPos }
                                : addedThing);
                            break;
                        case "Building":
                            this.world.addBuilding(this.networkClient && selection
                                ? { buildingType: selection.typeId, pos: clickPos }
                                : addedThing);
                            break;
                    }
                }
            } catch (error) {
                console.error("Canvas click error:", error);
                const et = new EffectText("放置出错！请刷新浏览器重试");
                et.pos = new Vector(this.world.width / 2, this.world.height / 2);
                this.world.addEffect(et);
            }
            this.callbacks.requestPauseRender();
        }, { signal: this.eventSignal });

        const updatePlacementPreview = (clientX: number, clientY: number): void => {
            if (this.addedThingFunc === null) {
                this.cachedBuilding = null;
                this.lastAddedFunc = null;
                return;
            }
            if (this.lastAddedFunc !== this.addedThingFunc) {
                this.cachedBuilding = this.addedThingFunc(this.world);
                this.lastAddedFunc = this.addedThingFunc;
            }
            this.world.user.putLoc.building = this.cachedBuilding;

            const rect = this.canvasEle.getBoundingClientRect();
            const screenPos = new Vector(clientX - rect.left, clientY - rect.top);
            const worldPos = this.world.camera.screenToWorld(screenPos);

            this.world.user.putLoc.x = worldPos.x;
            this.world.user.putLoc.y = worldPos.y;
            this.callbacks.requestPauseRender();
        };

        // Mouse move handler
        this.canvasEle.addEventListener('mousemove', (e) => {
            updatePlacementPreview(e.clientX, e.clientY);
        }, { signal: this.eventSignal });

        // Keep the placement ghost under a finger while the map is being moved.
        // A two-finger gesture is reserved for camera pan/zoom and must not move the ghost.
        this.canvasEle.addEventListener('touchmove', (e) => {
            if (e.touches.length !== 1) return;
            const touch = e.touches[0];
            updatePlacementPreview(touch.clientX, touch.clientY);
        }, { signal: this.eventSignal, passive: true });
    }

    private canMoveTargetTo(target: GameEntity, clickPos: Vector): boolean {
        const territory = this.world.territory;
        if (!territory?.markDirty || !territory?.recalculate) {
            return territory?.isPositionInValidTerritory ? territory.isPositionInValidTerritory(clickPos) : true;
        }

        const originalPos = target.pos.copy();
        try {
            target.pos = clickPos.copy();
            territory.markDirty();
            territory.recalculate();
            return target.inValidTerritory !== false;
        } finally {
            target.pos = originalPos;
            territory.markDirty();
            territory.recalculate();
        }
    }

    /**
     * 处理移动模式的点击逻辑
     */
    private handleMoveMode(clickPos: Vector, screenPos: Vector): void {
        // 第一次点击：选择要移动的建筑
        if (this.moveTarget === null) {
            // 检查是否点击了建筑（不包括矿井）
            for (const item of this.world.getAllBuildingArr()) {
                if (item.getBodyCircle().pointIn(clickPos.x, clickPos.y)) {
                    // 矿井不能移动
                    if (item.gameType === "Mine") {
                        const et = new EffectText("矿井不能移动！");
                        et.pos = clickPos.copy();
                        this.world.addEffect(et);
                        return;
                    }
                    // 检查是否在有效领地内（直接使用建筑的 inValidTerritory 属性）
                    if (item.inValidTerritory === false) {
                        const et = new EffectText("只能移动有效领地内的建筑！");
                        et.pos = clickPos.copy();
                        this.world.addEffect(et);
                        return;
                    }
                    // 选中建筑
                    this.moveTarget = item as unknown as GameEntity;
                    item.selected = true;
                    // 设置渲染器用的移动目标信息
                    const bodyCircle = item.getBodyCircle();
                    this.world.user.moveTarget = {
                        x: bodyCircle.x,
                        y: bodyCircle.y,
                        r: bodyCircle.r
                    };
                    const et = new EffectText("已选中，点击目标位置移动");
                    et.pos = clickPos.copy();
                    this.world.addEffect(et);
                    return;
                }
            }
            // 没有点击到建筑，取消选中
            this.moveTarget = null;
            this.world.user.moveTarget = null;
            return;
        }

        // 第二次点击：移动到目标位置
        const distance = this.moveTarget.pos.dis(clickPos);

        // 检查距离
        if (distance > MOVE_MAX_DISTANCE) {
            const et = new EffectText(`距离太远！最大${MOVE_MAX_DISTANCE}像素`);
            et.pos = clickPos.copy();
            this.world.addEffect(et);
            return;
        }

        // 检查目标位置是否在有效领地内（基于移动后的真实结果，而不是旧位置的领地状态）
        if (!this.canMoveTargetTo(this.moveTarget, clickPos)) {
            const et = new EffectText("目标位置不在有效领地内！");
            et.pos = clickPos.copy();
            this.world.addEffect(et);
            return;
        }

        // 检查碰撞：其他建筑
        const originalCircle = this.moveTarget.getBodyCircle();
        const targetCircle = new Circle(clickPos.x, clickPos.y, originalCircle.r ?? this.moveTarget.r ?? 20);
        for (const item of this.world.getAllBuildingArr()) {
            if ((item as unknown) === (this.moveTarget as unknown)) continue; // 跳过自己
            if (targetCircle.impact(item.getBodyCircle())) {
                const et = new EffectText("目标位置有其他建筑！");
                et.pos = clickPos.copy();
                this.world.addEffect(et);
                return;
            }
        }

        // 检查碰撞：障碍物
        for (const obs of this.world.obstacles) {
            if (obs.intersectsCircle(targetCircle)) {
                const et = new EffectText("目标位置有障碍物！");
                et.pos = clickPos.copy();
                this.world.addEffect(et);
                return;
            }
        }

        // 检查金币
        if (this.world.getMoney() < MOVE_COST) {
            const et = new EffectText(`金币不足！需要${MOVE_COST}元`);
            et.pos = clickPos.copy();
            this.world.addEffect(et);
            return;
        }

        // 执行移动
        this.moveTarget.pos = clickPos.copy();

        // 重新计算领地，避免增量更新在移动场景下残留错误状态
        this.world.territory?.markDirty?.();
        this.world.territory?.recalculate?.();

        // 更新建筑空间索引，避免碰撞/索敌继续使用旧位置
        this.world.markBuildingQuadTreeDirty?.();

        // 标记迷雾需要更新
        this.world.fog?.markDirty?.();

        // 标记静态层需要重建（建筑位置已改变）
        this.world.markStaticLayerDirty();

        // 清理放置预览，避免残留幽灵炮塔预览
        this.world.user.putLoc.building = null;
        this.cachedBuilding = null;
        this.lastAddedFunc = null;

        // 扣除金币
        this.world.spendMoney(MOVE_COST);

        // 取消选中状态
        this.moveTarget.selected = false;

        // 显示成功提示
        const et = new EffectText("移动成功！");
        et.pos = clickPos.copy();
        this.world.addEffect(et);

        // 9. 退出移动模式
        this.moveMode = false;
        this.moveTarget = null;
        this.world.user.moveTarget = null;
        this.setMoveCursor(false);
    }

    private setupRightClickHandler(): void {
        document.oncontextmenu = (e) => {
            if (e.button === 2) {
                this.addedThingFunc = null;
                this.placementSelection = null;
                this.moveMode = false;
                this.moveTarget = null;
                this.world.user.moveTarget = null;
                this.world.user.putLoc.building = null;
                this.cachedBuilding = null;
                this.lastAddedFunc = null;
                this.setMoveCursor(false);
                this.callbacks.requestPauseRender();
                return false;
            }
            return true;
        };
    }

    private startIntervals(): void {
        // Refresh panel interval - 由 destroy() 统一清理
        this.refreshPanelInterval = setInterval(() => {
            if (this.addedThingFunc === null && this.selectedThing === null) {
                this.showInitPanel();
            } else if (this.selectedThing !== null) {
                this.showSelectedPanel(false);
            }
        }, 100);

        // Button state update interval - 由 destroy() 统一清理
        this.freshBtnInterval = setInterval(() => {
            const towerBtnArr = document.getElementsByClassName("towerBtn");

            const basicCCreator = TowerRegistry.getCreator('BasicCannon') as ((world: unknown) => GameEntity) | undefined;
            const basicC = basicCCreator?.(this.world);
            if (basicC) {
                towerBtnArr[0]?.setAttribute("data-price", basicC.price.toString());
                if (towerBtnArr[0]) {
                    towerBtnArr[0].innerHTML = basicC.name + `<br>${basicC.price}￥`;
                }
            }
            for (let i = 0; i < towerBtnArr.length; i++) {
                const btn = towerBtnArr[i] as HTMLElement;
                if (parseInt(btn.dataset.price!) <= this.world.getMoney()) {
                    btn.removeAttribute("disabled");
                } else {
                    btn.setAttribute("disabled", "disabled");
                }
            }
            const cancelSelectBtn = document.getElementById("cancelSelect");
            if (cancelSelectBtn) {
                if (this.addedThingFunc === null) {
                    cancelSelectBtn.setAttribute("disabled", "disabled");
                } else {
                    cancelSelectBtn.removeAttribute("disabled");
                }
            }
            const itemArr = this.smallLevelUpPanelEle.getElementsByClassName("levelUpItem");
            for (let i = 0; i < itemArr.length; i++) {
                const itemEle = itemArr[i] as HTMLElement;
                if (parseInt(itemEle.dataset.price!) <= this.world.getMoney()) {
                    itemEle.removeAttribute("disabled");
                    itemEle.style.opacity = "1";
                } else {
                    itemEle.setAttribute("disabled", "disabled");
                    itemEle.style.opacity = "0.2";
                }
            }
        }, 100);
    }
}
